import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogService,
  CreateHospitalityPolicyExceptionPayload,
  HospitalityPolicyException,
  HospitalityProperty
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-hospitality-policy-exceptions',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule,
    TableModule, ButtonModule, SelectModule, InputTextModule, TextareaModule, DialogModule
  ],
  template: `
    <ain-page [title]="t('catalog.hospitality.policyExceptions.title')" [description]="t('catalog.hospitality.policyExceptions.description')">
      @if (loading() && rows().length === 0) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <p-select
              [options]="propertyOptions()"
              [(ngModel)]="selectedPropertyId"
              (ngModelChange)="load()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('catalog.hospitality.property')"
              [filter]="true"
              appendTo="body" />
            <p-select
              [options]="statusOptions"
              [(ngModel)]="statusFilter"
              (ngModelChange)="load()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('catalog.tracking.status')"
              appendTo="body" />
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
            <button pButton type="button" [label]="t('common.create')" [disabled]="!selectedPropertyId" (click)="openCreateDialog()"></button>
            <a routerLink="/hospitality/reservations">{{ t('catalog.hospitality.reservations.title') }}</a>
          </div>

          @if (!selectedPropertyId) {
            <ain-state type="empty" [title]="t('catalog.hospitality.policyExceptions.selectProperty')" />
          } @else if (rows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.hospitality.policyExceptions.empty')" />
          } @else {
            <p-table [value]="rows()" [paginator]="true" [rows]="20">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.hospitality.policyExceptions.exceptionType') }}</th>
                  <th>{{ t('catalog.hospitality.policyExceptions.reference') }}</th>
                  <th>{{ t('catalog.hospitality.policyExceptions.reason') }}</th>
                  <th>{{ t('catalog.tracking.status') }}</th>
                  <th>{{ t('catalog.hospitality.policyExceptions.approvedBy') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.exceptionType }}</td>
                  <td>{{ row.referenceEntityName }} · {{ row.referenceEntityId }}</td>
                  <td>{{ row.reason }}</td>
                  <td>{{ row.status }}</td>
                  <td>{{ row.approvedByReference || '—' }}</td>
                  <td class="actions">
                    @if (row.status === 'Pending') {
                      <button pButton type="button" size="small" [label]="t('catalog.hospitality.policyExceptions.resolve')" (click)="openResolveDialog(row)"></button>
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }

      <p-dialog
        [header]="t('catalog.hospitality.policyExceptions.createTitle')"
        [visible]="createDialogOpen()"
        [modal]="true"
        [style]="{ width: '36rem', maxWidth: '95vw' }"
        (visibleChange)="onCreateDialogVisibleChange($event)">
        <div class="form-grid">
          <label>
            <span>{{ t('catalog.hospitality.policyExceptions.exceptionType') }}</span>
            <p-select [options]="exceptionTypeOptions" [(ngModel)]="createForm.exceptionType" optionLabel="label" optionValue="value" appendTo="body" />
          </label>
          <label>
            <span>{{ t('catalog.hospitality.policyExceptions.referenceEntityName') }}</span>
            <input pInputText [(ngModel)]="createForm.referenceEntityName" />
          </label>
          <label class="full">
            <span>{{ t('catalog.hospitality.policyExceptions.referenceEntityId') }}</span>
            <input pInputText [(ngModel)]="createForm.referenceEntityId" />
          </label>
          <label class="full">
            <span>{{ t('catalog.hospitality.policyExceptions.reason') }}</span>
            <textarea pTextarea rows="3" [(ngModel)]="createForm.reason"></textarea>
          </label>
        </div>
        <div class="dialog-footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeCreateDialog()"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveException()"></button>
        </div>
      </p-dialog>

      <p-dialog
        [header]="t('catalog.hospitality.policyExceptions.resolveTitle')"
        [visible]="resolveDialogOpen()"
        [modal]="true"
        [style]="{ width: '32rem', maxWidth: '95vw' }"
        (visibleChange)="onResolveDialogVisibleChange($event)">
        @if (pendingResolve(); as row) {
          <p>{{ row.exceptionType }} · {{ row.reason }}</p>
          <label class="full">
            <span>{{ t('catalog.hospitality.policyExceptions.approvedBy') }}</span>
            <input pInputText [(ngModel)]="resolveReference" />
          </label>
        }
        <div class="dialog-footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeResolveDialog()"></button>
          <button pButton type="button" severity="danger" [label]="t('catalog.hospitality.policyExceptions.reject')" [disabled]="saving()" (click)="resolve(false)"></button>
          <button pButton type="button" [label]="t('catalog.hospitality.policyExceptions.approve')" [disabled]="saving()" (click)="resolve(true)"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 12px; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .toolbar a { color: #1d4ed8; font-weight: 600; text-decoration: none; margin-inline-start: auto; }
    .actions { display: flex; gap: 6px; }
    .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .form-grid label { display: grid; gap: 4px; font-size: 13px; }
    .form-grid label.full { grid-column: 1 / -1; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
  `]
})
export class HospitalityPolicyExceptionsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly rows = signal<HospitalityPolicyException[]>([]);
  readonly properties = signal<HospitalityProperty[]>([]);
  readonly createDialogOpen = signal(false);
  readonly resolveDialogOpen = signal(false);
  readonly pendingResolve = signal<HospitalityPolicyException | null>(null);

  selectedPropertyId = '';
  statusFilter = '';
  resolveReference = '';

  createForm = {
    exceptionType: 'Cancellation',
    referenceEntityName: 'HospitalityReservation',
    referenceEntityId: '',
    reason: ''
  };

  readonly statusOptions = [
    { label: 'All', value: '' },
    { label: 'Pending', value: 'Pending' },
    { label: 'Approved', value: 'Approved' },
    { label: 'Rejected', value: 'Rejected' }
  ];

  readonly exceptionTypeOptions = [
    { label: 'Cancellation', value: 'Cancellation' },
    { label: 'NoShow', value: 'NoShow' },
    { label: 'Overbooking', value: 'Overbooking' },
    { label: 'RateOverride', value: 'RateOverride' }
  ];

  readonly propertyOptions = computed(() =>
    this.properties().map(item => ({
      value: item.id,
      label: item.propertyCode ? `${item.propertyCode} — ${item.name}` : item.name
    })));

  ngOnInit(): void {
    this.service.listHospitalityProperties().subscribe({
      next: rows => {
        this.properties.set(rows);
        if (rows.length === 1) {
          this.selectedPropertyId = rows[0].id;
          this.load();
        } else {
          this.loading.set(false);
        }
      },
      error: () => this.loading.set(false)
    });
  }

  load(): void {
    if (!this.selectedPropertyId) {
      this.rows.set([]);
      return;
    }

    this.loading.set(true);
    this.service.listPolicyExceptions(this.selectedPropertyId, this.statusFilter || undefined).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  openCreateDialog(): void {
    this.createForm = {
      exceptionType: 'Cancellation',
      referenceEntityName: 'HospitalityReservation',
      referenceEntityId: '',
      reason: ''
    };
    this.createDialogOpen.set(true);
  }

  onCreateDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeCreateDialog();
  }

  closeCreateDialog(): void {
    this.createDialogOpen.set(false);
  }

  saveException(): void {
    if (!this.selectedPropertyId || !this.createForm.reason.trim() || !this.createForm.referenceEntityId.trim()) {
      return;
    }

    const payload: CreateHospitalityPolicyExceptionPayload = {
      propertyId: this.selectedPropertyId,
      exceptionType: this.createForm.exceptionType,
      referenceEntityName: this.createForm.referenceEntityName.trim(),
      referenceEntityId: this.createForm.referenceEntityId.trim(),
      reason: this.createForm.reason.trim()
    };

    this.saving.set(true);
    this.service.createPolicyException(payload).subscribe({
      next: () => {
        this.saving.set(false);
        this.closeCreateDialog();
        this.load();
      },
      error: () => this.saving.set(false)
    });
  }

  openResolveDialog(row: HospitalityPolicyException): void {
    this.pendingResolve.set(row);
    this.resolveReference = '';
    this.resolveDialogOpen.set(true);
  }

  onResolveDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeResolveDialog();
  }

  closeResolveDialog(): void {
    this.resolveDialogOpen.set(false);
    this.pendingResolve.set(null);
    this.resolveReference = '';
  }

  resolve(approve: boolean): void {
    const row = this.pendingResolve();
    if (!row) return;

    this.saving.set(true);
    this.service.resolvePolicyException(row.id, {
      approve,
      approvedByReference: this.resolveReference.trim() || 'admin'
    }).subscribe({
      next: () => {
        this.saving.set(false);
        this.closeResolveDialog();
        this.load();
      },
      error: () => this.saving.set(false)
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
